import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import PlaylistData from "../playlist/data/PlaylistData";
import { PlaylistItemData } from "../playlist/data/PlaylistItemData";
import { useCrewLiveContext } from "../live/CrewLiveContext";
import { PlaylistLivePositionPayload } from "../live/playlist/PlaylistLivePositionPayload";
import { LivePositionMarker } from "../live/LivePositionMarker";
import { useDeviceContext } from "../device/DeviceInfoContext";
import { CyPlaylistBanner } from "./CyPlaylistBanner";
import { LookupSongFunction } from "../common/LookupSongFunction";
import { useDragToScroll } from "./useDragToScroll";
import { useScrollEdges } from "./useScrollEdges";
import "./PlaylistBanner.css";

interface Props {
  playlistData: PlaylistData;
  selectedItem: PlaylistItemData | undefined;
  setSelectedItem: (item: PlaylistItemData) => void;
  lookupSong: LookupSongFunction;
}

const maxTitleLength = 30;

function shortenTitle(title: string): string {
  return title.length > maxTitleLength
    ? title.slice(0, maxTitleLength).trimEnd() + "…"
    : title;
}

// The active item is centered in the banner, so its neighbours on both sides stay visible.
// Only a change of the active item scrolls, the user is free to pan the banner in between.
export default function PlaylistBanner({
  lookupSong,
  playlistData,
  selectedItem,
  setSelectedItem,
}: Props) {
  const { t } = useTranslation();

  const { playlistLivePosition } = useCrewLiveContext();

  const { deviceIdentifier } = useDeviceContext();

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const activeItemRef = useRef<HTMLButtonElement | null>(null);
  const hasScrolledOnceRef = useRef(false);

  useDragToScroll(scrollerRef);

  const items = playlistData.getItems();

  const { canScrollToStart, canScrollToEnd } = useScrollEdges(
    scrollerRef,
    items.length,
  );

  useEffect(() => {
    const scroller = scrollerRef.current;
    const activeItem = activeItemRef.current;
    if (!scroller || !activeItem) return;
    scroller.scrollTo({
      left:
        activeItem.offsetLeft -
        (scroller.clientWidth - activeItem.offsetWidth) / 2,
      // jumping into place on the first render, smooth afterwards to keep the orientation
      behavior: hasScrolledOnceRef.current ? "smooth" : "auto",
    });
    hasScrolledOnceRef.current = true;
  }, [selectedItem?.id, items.length]);

  function generateDeviceInfo(itemId: string): React.ReactNode {
    return Array.from(playlistLivePosition.values())
      .filter((e) => {
        return (
          e.playlistIdentifier === playlistData.id &&
          e.deviceIdentifier !== deviceIdentifier && // exclude own device
          e.selectedPlaylistItemId !== undefined &&
          e.selectedPlaylistItemId === itemId
        );
      })
      .map((p: PlaylistLivePositionPayload) => {
        return (
          <LivePositionMarker
            color={p.deviceColor}
            key={p.deviceIdentifier}
            deviceIdentifier={p.deviceIdentifier}
            className="ms-1"
          />
        );
      });
  }

  return (
    <div data-cy={CyPlaylistBanner.PlaylistBanner} className="playlist-banner">
      <div
        className={
          "playlist-banner-scroller" +
          (canScrollToStart ? " fade-start" : "") +
          (canScrollToEnd ? " fade-end" : "")
        }
        ref={scrollerRef}
      >
        {items.map((playlistEntry) => {
          const toCEntry = lookupSong(playlistEntry.songEntryId);
          const title = toCEntry
            ? toCEntry.title
            : t("playlist.item.invalidEntry");

          const active = playlistEntry.id === selectedItem?.id;
          return (
            <button
              type="button"
              data-cy={CyPlaylistBanner.PlaylistBannerItem}
              className={
                "btn rounded-pill playlist-banner-item " +
                (active ? "btn-warning" : "btn-secondary")
              }
              aria-current={active ? "true" : undefined}
              title={title}
              ref={active ? activeItemRef : undefined}
              onClick={() => setSelectedItem(playlistEntry)}
              key={playlistEntry.id}
            >
              {shortenTitle(title)}
              {generateDeviceInfo(playlistEntry.id)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
